import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Hammer, MessageCircle, Phone } from "lucide-react";
import { DIVISIONS } from "@/data/catalog";
import { useContact } from "@/context/ContactContext";
import { trackEvent } from "@/lib/analytics";

const link = "text-[15px] font-semibold text-slate-700 hover:text-[#B87333] transition-colors";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const contact = useContact();
  const waNum = (contact.whatsapp || "").replace(/[^0-9]/g, "");
  const waHref = waNum ? `https://wa.me/${waNum}` : "/request-quotation";
  const telHref = `tel:+${(contact.phone || "").replace(/[^0-9]/g, "")}`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setServicesOpen(false); }, [location.pathname]);

  return (
    <header data-testid="site-navbar" className={`fixed top-0 inset-x-0 z-50 bg-white transition-shadow ${scrolled ? "shadow-md" : "border-b border-slate-100"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-10 h-10 rounded-md bg-[#0A1128] text-[#D97706]"><Hammer size={21} strokeWidth={2.5} /></span>
            <span className="leading-none">
              <span className="block font-display font-extrabold text-[#0A1128] text-lg tracking-tight">WAAN METAL</span>
              <span className="block text-[10px] font-mono-label uppercase tracking-[0.18em] text-[#B87333]">Renovation · Build · Metal</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            <Link to="/" className={link} data-testid="nav-home">Home</Link>
            <Link to="/about" className={link} data-testid="nav-about">About</Link>
            <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
              <button className={`${link} flex items-center gap-1`} data-testid="nav-services">Services <ChevronDown size={15} /></button>
              {servicesOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-64" data-testid="nav-services-dropdown">
                  <div className="bg-white rounded-xl shadow-2xl border border-slate-100 py-2">
                    {DIVISIONS.map((d) => (
                      <Link key={d.slug} to={`/services/${d.slug}`} data-testid={`nav-service-${d.slug}`} className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#B87333]">{d.title}</Link>
                    ))}
                    <Link to="/interior-design" data-testid="nav-service-interior-design" className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#B87333]">Interior Design</Link>
                  </div>
                </div>
              )}
            </div>
            <Link to="/price-guide" className={`${link} text-[#B87333]`} data-testid="nav-price-guide">Price Guide</Link>
            <Link to="/projects" className={link} data-testid="nav-projects">Projects</Link>
            <Link to="/contact" className={link} data-testid="nav-contact">Contact</Link>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {contact.phone ? (
              <a href={telHref} data-testid="nav-phone" className="hidden xl:inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-[#B87333] transition-colors">
                <Phone size={16} /> {contact.phone}
              </a>
            ) : null}
            <a href={waHref} target={waNum ? "_blank" : undefined} rel="noopener noreferrer" onClick={() => trackEvent("whatsapp_click", { source: "navbar" })} data-testid="nav-whatsapp" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 text-slate-700 text-sm font-semibold hover:border-[#25D366] hover:text-[#25D366] transition-colors">
              <MessageCircle size={16} /> WhatsApp
            </a>
            <Link to="/request-quotation" data-testid="nav-request-quotation-button" className="px-5 py-2.5 rounded-full bg-[#B87333] text-white text-sm font-bold hover:bg-[#a9652c] transition-colors">Request a Quote</Link>
          </div>

          <button className="lg:hidden text-[#0A1128]" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Menu">{open ? <X size={26} /> : <Menu size={26} />}</button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-1" data-testid="nav-mobile-menu">
          <Link to="/" className="block py-2.5 text-slate-700 font-medium">Home</Link>
          <Link to="/about" className="block py-2.5 text-slate-700 font-medium">About</Link>
          <p className="eyebrow pt-2 pb-1">Services</p>
          {DIVISIONS.map((d) => <Link key={d.slug} to={`/services/${d.slug}`} className="block py-2 pl-3 text-slate-600 text-sm">{d.title}</Link>)}
          <Link to="/interior-design" className="block py-2 pl-3 text-slate-600 text-sm">Interior Design</Link>
          <Link to="/price-guide" className="block py-2.5 text-[#B87333] font-semibold">Price Guide</Link>
          <Link to="/projects" className="block py-2.5 text-slate-700 font-medium">Projects</Link>
          <Link to="/contact" className="block py-2.5 text-slate-700 font-medium">Contact</Link>
          {contact.phone ? (
            <a href={telHref} data-testid="nav-mobile-phone" className="flex items-center gap-2 py-2.5 text-slate-700 font-medium"><Phone size={16} className="text-[#B87333]" /> {contact.phone}</a>
          ) : null}
          <div className="flex gap-3 pt-3">
            <a href={waHref} target={waNum ? "_blank" : undefined} rel="noopener noreferrer" className="flex-1 text-center px-4 py-3 rounded-full border border-slate-200 text-slate-700 font-semibold">WhatsApp</a>
            <Link to="/request-quotation" data-testid="nav-mobile-quotation-button" className="flex-1 text-center px-4 py-3 rounded-full bg-[#B87333] text-white font-bold">Request a Quote</Link>
          </div>
        </div>
      )}
    </header>
  );
};
